import { adminPath, type AdminClient, type ApiResponse } from '../core/index.js'
import type { ActivationResponse, ClientAddOn, Entitlement, FeatureDefinition, FeatureLicense, LicenseActionInput, UpsertLicenseInput } from './types.js'

/** An admin mirror's answer: the data, and where the add-on stands at the facility (`feature`). */
export interface Marked<T> {
  data: T
  feature: Entitlement
}

/** Reads one of an add-on's admin mirrors, which answer the usual envelope plus the add-on's marker. For an add-on's own admin calls. */
export async function readMarked<T>(client: AdminClient, path: string): Promise<Marked<T>> {
  const envelope = await client.call<ApiResponse<T> & { feature: Entitlement }>('GET', path, undefined, true)
  return { data: envelope.data as T, feature: envelope.feature }
}

/** The add-ons module's admin calls: the catalog, a client's licenses and the switch per facility (docs/plans/feature-licensing-plan.md). */
export interface AddOnsAdminApi {
  /** The add-on catalog: what can be licensed. */
  listAddOns(): Promise<FeatureDefinition[]>
  /** A client's license and facility switches for every add-on. */
  clientAddOns(clientId: string): Promise<ClientAddOn[]>
  /** Creates a client's license for an add-on, or updates it when `version` is the one last read. A sales login's is always recorded as a demo license. */
  upsertLicense(clientId: string, key: string, input: UpsertLicenseInput): Promise<FeatureLicense>
  licenseAction(clientId: string, key: string, action: 'suspend' | 'resume' | 'end', input: LicenseActionInput): Promise<FeatureLicense>
  /** The switch for one facility, on the client's behalf. */
  setFacilityAddOn(clientId: string, facilityId: string, key: string, enabled: boolean): Promise<ActivationResponse>
}

export function createAddOnsAdminApi(client: AdminClient): AddOnsAdminApi {
  const license = (clientId: string, key: string): string => `${adminPath.client(clientId)}/add-ons/${encodeURIComponent(key)}/license`
  return {
    listAddOns: () => client.call('GET', '/admin/add-ons'),
    clientAddOns: (clientId) => client.call('GET', `${adminPath.client(clientId)}/add-ons`),
    upsertLicense: (clientId, key, input) => client.call('PUT', license(clientId, key), input),
    licenseAction: (clientId, key, action, input) => client.call('POST', `${license(clientId, key)}/${action}`, input),
    setFacilityAddOn: (clientId, facilityId, key, enabled) => client.call('PUT', `${adminPath.facility(clientId, facilityId)}/add-ons/${encodeURIComponent(key)}`, { enabled }),
  }
}
